import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  useBookingsByIdsNavigation,
  useRidesByIdsNavigation,
} from "@ryde/redux";
import { IProductionStateLogData, safeNumber } from "@ryde/shared";

import { ProductionLogsHubClickableCount } from "./production-logs.hub-clickable-count";

interface IProductionLogsStatsTabsMetricsHubProps {
  state?: IProductionStateLogData;
}

export const ProductionLogsStatsTabsMetricsHub: FC<
  IProductionLogsStatsTabsMetricsHubProps
> = ({ state }) => {
  const { t } = useTranslation();
  const {
    hubBookingIds,
    hubBookingCount,
    nonHubBookingCount,
    hubMainRideIds,
    hubMainRideCount,
    hubPersonalRideIds,
    hubPersonalRideCount,
    hubExpectedMainRideCount,
    hubExpectedPersonalRideCount,
    hubBookingsSkippedInSplitIds,
    hubBookingsSkippedInSplitCount,
    hubPersonalGeoFailedBookingIds,
    hubPersonalGeoFailedCount,
    hubBookingsWithMainSegmentIds,
    hubBookingsWithMainSegmentCount,
    hubIncompletePairingBookingIds,
    hubIncompletePairingCount,
    hubBookingsProducedIds,
    hubBookingsProducedCount,
    hubCompanionBookingIds,
    hubCompanionBookingCount,
    hubBookingsExcludedFromJoinIds,
    hubExplicitSkipBookingIds,
    hubExplicitSkipBookingCount,
  } = state || {};
  const { navigateToRidesByIds } = useRidesByIdsNavigation();
  const { navigateToBookingsByIds } = useBookingsByIdsNavigation();

  const mainRideCount = safeNumber(hubMainRideCount);
  const personalRideCount = safeNumber(hubPersonalRideCount);
  const expectedMain = safeNumber(hubExpectedMainRideCount);
  const expectedPersonal = safeNumber(hubExpectedPersonalRideCount);
  const skippedCount = safeNumber(hubBookingsSkippedInSplitCount);
  const mainSegmentCount =
    hubBookingsWithMainSegmentIds?.length ??
    safeNumber(hubBookingsWithMainSegmentCount);
  const geoFailedCount =
    hubPersonalGeoFailedBookingIds?.length ??
    safeNumber(hubPersonalGeoFailedCount);
  const incompleteCount = safeNumber(hubIncompletePairingCount);
  const producedCount =
    hubBookingsProducedIds?.length ?? safeNumber(hubBookingsProducedCount);
  const companionCount =
    hubCompanionBookingIds?.length ?? safeNumber(hubCompanionBookingCount);
  const joinExcludedCount = hubBookingsExcludedFromJoinIds?.length ?? 0;
  const explicitSkipCount =
    hubExplicitSkipBookingIds?.length ??
    safeNumber(hubExplicitSkipBookingCount);

  if (
    !safeNumber(hubBookingCount) &&
    !mainRideCount &&
    !personalRideCount &&
    !mainSegmentCount &&
    !skippedCount &&
    !geoFailedCount &&
    !incompleteCount &&
    !producedCount &&
    !companionCount &&
    !joinExcludedCount &&
    !explicitSkipCount
  ) {
    return null;
  }

  return (
    <>
      <div className="log__metrics-section-title">
        {t("rideProduction.hubStatisticsTitle")}
      </div>
      <div className="log__metrics-map-stats">
        {Boolean(nonHubBookingCount) && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              {t("rideProduction.hubNonHubBookings")}:
            </span>
            <span className="log__metrics-map-value log__metrics-map-value-booking">
              {nonHubBookingCount}
            </span>
          </div>
        )}
        {explicitSkipCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              {t("rideProduction.hubExplicitSkip")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubExplicitSkipBookingIds}
              count={explicitSkipCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {safeNumber(hubBookingCount) > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              {t("rideProduction.hubBookingsCount")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubBookingIds}
              count={safeNumber(hubBookingCount)}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {expectedMain > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">
              {t("rideProduction.hubExpectedMainRides")}:
            </span>
            <span className="log__metrics-map-value">
              {mainRideCount} / {expectedMain}
            </span>
          </div>
        )}
        <div className="log__metrics-map-item">
          <span className="log__metrics-map-label log__metrics-map-label-hub-created">
            {t("rideProduction.hubMainRides")}:
          </span>
          <ProductionLogsHubClickableCount
            ids={hubMainRideIds}
            count={mainRideCount}
            onNavigate={navigateToRidesByIds}
            valueClassName="log__metrics-map-value"
            clickableClassName="log__metrics-map-value-clickable-green"
          />
        </div>
        {expectedPersonal > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">
              {t("rideProduction.hubExpectedPersonalRides")}:
            </span>
            <span className="log__metrics-map-value">
              {personalRideCount} / {expectedPersonal}
            </span>
          </div>
        )}
        <div className="log__metrics-map-item">
          <span className="log__metrics-map-label log__metrics-map-label-hub-created">
            {t("rideProduction.hubPersonalRides")}:
          </span>
          <ProductionLogsHubClickableCount
            ids={hubPersonalRideIds}
            count={personalRideCount}
            onNavigate={navigateToRidesByIds}
            valueClassName="log__metrics-map-value"
            clickableClassName="log__metrics-map-value-clickable-green"
          />
        </div>
        {mainSegmentCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              {t("rideProduction.hubBookingsWithMainSegment")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubBookingsWithMainSegmentIds}
              count={mainSegmentCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {producedCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-hub-routed">
              {t("rideProduction.hubBookingsProduced")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubBookingsProducedIds}
              count={producedCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {companionCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-hub-routed">
              {t("rideProduction.hubCompanionBookings")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubCompanionBookingIds}
              count={companionCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {skippedCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              {t("rideProduction.hubBookingsSkippedInSplit")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubBookingsSkippedInSplitIds}
              count={skippedCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {geoFailedCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label">
              {t("rideProduction.hubPersonalGeoFailed")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubPersonalGeoFailedBookingIds}
              count={geoFailedCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {incompleteCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              {t("rideProduction.hubIncompletePairing")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubIncompletePairingBookingIds}
              count={incompleteCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
        {joinExcludedCount > 0 && (
          <div className="log__metrics-map-item">
            <span className="log__metrics-map-label log__metrics-map-label-booking">
              {t("rideProduction.hubBookingsExcludedFromJoin")}:
            </span>
            <ProductionLogsHubClickableCount
              ids={hubBookingsExcludedFromJoinIds}
              count={joinExcludedCount}
              onNavigate={navigateToBookingsByIds}
              valueClassName="log__metrics-map-value log__metrics-map-value-booking"
              clickableClassName="log__metrics-map-value-clickable-green log__metrics-map-value-booking"
            />
          </div>
        )}
      </div>
    </>
  );
};
